import type { FilterChip } from './filter-chips';
import type { SelectOption } from './select-field';

/**
 * 필터 칩 라벨 구분자 — `차원: 값`(전체/선택·날짜칩 공통). 스키마가 자동 생성하는 칩뿐 아니라 **feature 가 직접
 * 조립하는 `chip` override**(인버터·보기 방식 등)도 이걸 쓴다 — 손으로 `': '` 를 적기 시작하면 곧 갈린다.
 */
export const CHIP_LABEL_SEP = ': ';

/**
 * 필터 한 **논리 차원**의 선언 — Applied(`A`) 상태에서 칩을 파생한다(도메인 무지 제네릭).
 *
 * **2층 구조**:
 * - **자동(간단 차원)**: `neutral`(+선택 `format`)만 주면 칩·제거가 자동 생성된다. `applied[key]` 가 `neutral`
 *   이면 비활성(칩 없음), 아니면 칩(라벨=`format(applied)` 또는 `「label: 값」`)·제거 시 `key`→`neutral` 복원.
 *   값이 자기설명적(텍스트·boolean·코드→라벨)일 때만 유효 — 표시는 `format` 이 책임.
 * - **override(예외 차원)**: `chip` 을 직접 주면 위 자동 경로를 무시. **결합**(여러 key→칩 1개, 예: 날짜 범위·
 *   발전소+인버터)·**cascade 제거**(부모 해제 시 자식 동시)·**조건부**(로딩 중 억제 등) 같은 갭이 있는 차원용.
 *
 * **컨트롤(input)은 스키마가 그리지 않는다** — 컨트롤은 feature 가 손으로(라벨은 `UI_TEXT` 공유). 스키마는 투영만.
 *
 * **배지(activeCount)는 별도 선언하지 않는다** — 「삭제 가능한 칩 수」로 파생(`countRemovableChips`). 보이는 x
 * 개수 = 배지 숫자. (항상 존재해 제거 불가한 차원(예: 날짜)은 칩은 보이되 배지엔 안 셈.)
 */
export interface FilterFieldSpec<A> {
  /** 칩 key + 차원 식별(안정). 자동 경로에선 `A` 의 단일 필드명이어야 한다(읽기·제거 대상). */
  key: string;
  /** 차원의 SSOT 이름 — `UI_TEXT` 참조. 자동 칩의 「label: 값」 라벨. */
  label: string;
  /** 자동: 중립/기본값. `applied[key]` 가 이 값(또는 null/'')이면 비활성. 제거 시 이 값으로 복원. */
  neutral?: unknown;
  /** 자동: 값 표시 텍스트(주면 칩 라벨 전체를 이게 담당). `null` = 비활성(칩 없음). 없으면 `「label: 원시값」`. */
  format?: (applied: A) => string | null;
  /** override: 칩 직접 생성(결합·cascade·조건부). 주면 `neutral`/`format` 무시. `null` = 칩 없음. */
  chip?: (applied: A) => { label: string; removePatch?: Partial<A> | null } | null;
}

/** spec 한 개 → 칩 데이터({label, removePatch}) 또는 null. override(chip) 우선, 없으면 자동(neutral/format). */
function resolveChip<A>(
  spec: FilterFieldSpec<A>,
  applied: A,
): { label: string; removePatch: Partial<A> | null } | null {
  if (spec.chip) {
    const c = spec.chip(applied);
    return c ? { label: c.label, removePatch: c.removePatch ?? null } : null;
  }
  // 자동 경로 — key 는 A 의 단일 필드(읽기/복원 대상).
  const current = (applied as Record<string, unknown>)[spec.key];
  const text = spec.format
    ? spec.format(applied)
    : current === spec.neutral || current == null || current === ''
      ? null
      : `${spec.label}${CHIP_LABEL_SEP}${String(current)}`;
  if (text == null) return null;
  return { label: text, removePatch: { [spec.key]: spec.neutral } as Partial<A> };
}

/**
 * spec 목록 → 활성 칩(전 breakpoint). 제거 가능 차원은 `onRemove` = `apply(중립패치 머지)`.
 * feature 별 `build-*-chips` 를 대체한다(칩 투영·제거 정책의 단일 흐름).
 */
export function buildFilterChips<A>(
  specs: FilterFieldSpec<A>[],
  applied: A,
  apply: (next: A) => void,
): FilterChip[] {
  const chips: FilterChip[] = [];
  for (const spec of specs) {
    const r = resolveChip(spec, applied);
    if (!r) continue;
    const patch = r.removePatch;
    chips.push({
      key: spec.key,
      label: r.label,
      onRemove: patch ? () => apply({ ...applied, ...patch }) : undefined,
    });
  }
  return chips;
}

/** 트리거 배지 — 「삭제 가능한 칩 수」(보이는 x 개수). 제거 불가 칩(예: 날짜)은 안 셈. */
export function countRemovableChips(chips: FilterChip[]): number {
  return chips.filter((c) => c.onRemove != null).length;
}

/**
 * 코드 단일선택 차원 → 칩 spec(override 경로 DRY). 전 필터 공통 「전체/기본값 항상 표시」 규칙을 담는다.
 * - 중립(`''`) = `차원:전체` **비제거 칩**(× 없음·배지 미집계, 옵션 로딩과 무관하게 항상 표시).
 * - 값 + 라벨 해석됨 = `차원:라벨` 제거 칩(제거=`removePatch`, 기본 `{[key]:''}`).
 * - 값 + 라벨 미해석(옵션 로딩 중) = 칩 억제(raw 코드 노출 방지 — 필터 UI 규칙 ⑩).
 *
 * `defaultValue`(비어있지 않은 기본값 — 예: 이벤트 등급 심각·상태 발생)를 주면 **오직 그 값일 때만 비제거 칩**
 * (× 없음·배지 미집계). 그 외 **모든 비기본값(전체 '' 포함)은 제거 칩**(× → 기본값 복원, ''가 아님) — isDirty(≠기본)와
 * 배지·칩 제거성이 일치한다. 기본값 없는 차원은 종전대로 전체('')=중립 비제거·선택=제거('').
 *
 * cascade(부모 해제 시 자식 동시 중립)는 `removePatch` 로 표현한다(예: 시도 제거 시 시군구·읍면동 동시).
 */
export function codeSelectChip<A>(config: {
  key: string;
  label: string;
  options: SelectOption[];
  /** '전체' 텍스트(`UI_TEXT.FILTER_ALL`). */
  allText: string;
  /** 제거 패치(기본 `{[key]: defaultValue ?? ''}`). cascade 시 자식 키도 포함. */
  removePatch?: Partial<A>;
  /** 비어있지 않은 기본값(심각/발생 등) — 이 값이면 비제거 칩, 그 외(전체 포함)는 제거 시 이 값으로 복원. */
  defaultValue?: string;
}): FilterFieldSpec<A> {
  const { key, label, options, allText, removePatch, defaultValue } = config;
  // 비기본값의 제거 패치 — 기본값 있으면 그 값 복원, 없으면 중립(''). (cascade removePatch 우선.)
  const restorePatch = removePatch ?? ({ [key]: defaultValue ?? '' } as Partial<A>);
  return {
    key,
    label,
    chip: (applied) => {
      const value = (applied as Record<string, unknown>)[key] as string;
      if (!value) {
        // 전체('') — 기본값이 별도(심각/발생)면 '비기본' 선택이라 제거 칩(× → 기본 복원). 기본값이 곧 전체면 중립 비제거.
        return defaultValue != null
          ? { label: `${label}${CHIP_LABEL_SEP}${allText}`, removePatch: restorePatch }
          : { label: `${label}${CHIP_LABEL_SEP}${allText}` };
      }
      const optionLabel = options.find((o) => o.value === value)?.label;
      if (optionLabel == null) return null;
      const chipLabel = `${label}${CHIP_LABEL_SEP}${optionLabel}`;
      // 기본값(심각/발생)과 같으면 비제거(× 없음). 다르면 제거=기본값 복원.
      if (defaultValue != null && value === defaultValue) return { label: chipLabel };
      return { label: chipLabel, removePatch: restorePatch };
    },
  };
}

/**
 * 코드 복수선택 차원 → 칩 spec. 「전체/일부」 패턴 공통화.
 * - 전체(`[]`) = `차원:전체` **비제거 칩**(× 없음·배지 미집계).
 * - 일부 = `차원:라벨, 라벨` 제거 칩(제거 → 전체 복원). 라벨 미해석 코드는 제외, 전부 미해석이면 칩 억제.
 */
export function codeMultiSelectChip<A>(config: {
  key: string;
  label: string;
  options: SelectOption[];
  allText: string;
}): FilterFieldSpec<A> {
  const { key, label, options, allText } = config;
  return {
    key,
    label,
    chip: (applied) => {
      const values = ((applied as Record<string, unknown>)[key] as string[]) ?? [];
      if (!values.length) return { label: `${label}${CHIP_LABEL_SEP}${allText}` };
      const labels = values
        .map((code) => options.find((o) => o.value === code)?.label)
        .filter((l): l is string => l != null);
      if (!labels.length) return null;
      return {
        label: `${label}${CHIP_LABEL_SEP}${labels.join(', ')}`,
        removePatch: { [key]: [] } as Partial<A>,
      };
    },
  };
}

/**
 * 키워드(search) 차원 → 칩 spec(자동 경로) — 여느 칩처럼 `label: 검색어` prefix 표기(`CHIP_LABEL_SEP` SSOT,
 * 선택·날짜칩 정합). neutral '' = 빈 검색 비활성, 제거 = 빈 검색. 필터 schema 들의 verbatim search spec 을 흡수.
 */
export function keywordChip<A extends { search: string }>(label: string): FilterFieldSpec<A> {
  return {
    key: 'search',
    label,
    neutral: '',
    format: (f) => (f.search ? `${label}${CHIP_LABEL_SEP}${f.search}` : null),
  };
}
